import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link2 } from "lucide-react";
import type { TransitionMeasure } from "@/contexts/wizard/types";
import { TRANSITION_CATALOG } from "@shared/transitionCatalog";

export interface TransitionReusePickerProps {
  currentTopicId: string;
  allMeasures: TransitionMeasure[]; // flattened across every topic in the assessment
  disabled?: boolean;
  onReuse: (measure: TransitionMeasure) => void;
}

export function TransitionReusePicker({ currentTopicId, allMeasures, disabled, onReuse }: TransitionReusePickerProps) {
  const reusable = allMeasures.filter(
    (m) => m.topicId !== currentTopicId && m.checked && !m.reusedFromMeasureId
  );

  const labelFor = (m: TransitionMeasure) =>
    m.actionId === "other" ? (m.otherLabel || "Untitled") : TRANSITION_CATALOG[m.topicId]?.actions.find((a) => a.id === m.actionId)?.name || m.actionId;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={disabled || reusable.length === 0} className="rounded-2xl">
          <Link2 className="mr-2 h-4 w-4" /> Use a measure from another topic
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Reuse a measure</DialogTitle></DialogHeader>
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {reusable.length === 0 && <p className="text-sm text-slate-400">No measures available to reuse yet.</p>}
          {reusable.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => onReuse(m)}
              className="flex w-full flex-col rounded-xl border border-slate-200 px-3 py-2 text-left hover:bg-slate-50"
            >
              <span className="text-sm font-medium text-slate-800">{labelFor(m)}</span>
              <span className="text-xs text-slate-400">{TRANSITION_CATALOG[m.topicId]?.sectionTitle} • {m.status || "no status"}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
